/**
 * Modern Storefront menus configuration.
 *
 * Two menus, sharing the same shape:
 *   - "available" → Available Now tab (stock-backed)
 *   - "offers"    → Special Offers tab (isolated from Home; can also appear in Available Now)
 *
 * Each menu controls:
 *   - assignedCategories
 *   - hiddenPriceKeys: per-tab hide list for specific (variant|grade|screen|sub) combos
 *   - useOwnPricing + applyMargin + applyExchangeRate + ownPriceOverrides
 *   - useOwnShipping + ownShippingOptionIds  (rendered as "Delivery Options" in overlay)
 *   - useOwnStock + ownStock (Available Now only; Special Offers ignores stock anyway)
 *
 * Pure storage layer — no calculations are performed here. All pricing/grading
 * logic remains in the existing backend stores and devicePriceUtil.
 */
import { create } from "zustand";

export type ModernMenuId = "available" | "offers";

export interface ModernMenuConfig {
  assignedCategories: string[];
  hiddenPriceKeys: string[];
  useOwnPricing: boolean;
  applyMargin: boolean;
  applyExchangeRate: boolean;
  ownPriceOverrides: Record<string, number>;
  useOwnShipping: boolean;
  ownShippingOptionIds: string[];
  useOwnStock?: boolean;
  ownStock?: Record<string, number>;
}

export interface ModernMenusState {
  available: ModernMenuConfig;
  offers: ModernMenuConfig;
}

const KEY = "khays_modern_menus";

const EMPTY: ModernMenuConfig = {
  assignedCategories: [],
  hiddenPriceKeys: [],
  useOwnPricing: false,
  applyMargin: true,
  applyExchangeRate: true,
  ownPriceOverrides: {},
  useOwnShipping: false,
  ownShippingOptionIds: [],
  useOwnStock: false,
  ownStock: {},
};

const DEFAULTS: ModernMenusState = { available: { ...EMPTY }, offers: { ...EMPTY } };

function load(): ModernMenusState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return {
        available: { ...EMPTY, ...(p.available || {}) },
        offers: { ...EMPTY, ...(p.offers || {}) },
      };
    }
  } catch {}
  return DEFAULTS;
}

function persist(state: ModernMenusState) {
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch {}
}

interface Store extends ModernMenusState {
  _rev: number;
  patchMenu: (id: ModernMenuId, patch: Partial<ModernMenuConfig>) => void;
  toggleCategory: (id: ModernMenuId, cat: string) => void;
  togglePriceKeyHidden: (id: ModernMenuId, key: string) => void;
  setOwnPriceOverride: (id: ModernMenuId, key: string, price: number | null) => void;
  toggleShippingOption: (id: ModernMenuId, optId: string) => void;
  setOwnStockQty: (id: ModernMenuId, key: string, qty: number | null) => void;
  reload: () => void;
}

export const useModernMenusStore = create<Store>((set, get) => ({
  ...load(),
  _rev: 0,
  patchMenu: (id, patch) => {
    const next = { ...get()[id], ...patch };
    const state: ModernMenusState = { available: get().available, offers: get().offers, [id]: next } as any;
    persist(state);
    set({ ...state, _rev: get()._rev + 1 });
  },
  toggleCategory: (id, cat) => {
    const cur = get()[id];
    const has = cur.assignedCategories.includes(cat);
    const assignedCategories = has ? cur.assignedCategories.filter(c => c !== cat) : [...cur.assignedCategories, cat];
    get().patchMenu(id, { assignedCategories });
  },
  togglePriceKeyHidden: (id, key) => {
    const cur = get()[id];
    const has = cur.hiddenPriceKeys.includes(key);
    const hiddenPriceKeys = has ? cur.hiddenPriceKeys.filter(k => k !== key) : [...cur.hiddenPriceKeys, key];
    get().patchMenu(id, { hiddenPriceKeys });
  },
  setOwnPriceOverride: (id, key, price) => {
    const cur = get()[id];
    const ownPriceOverrides = { ...cur.ownPriceOverrides };
    if (price === null || Number.isNaN(price)) delete ownPriceOverrides[key];
    else ownPriceOverrides[key] = price;
    get().patchMenu(id, { ownPriceOverrides });
  },
  toggleShippingOption: (id, optId) => {
    const cur = get()[id];
    const has = cur.ownShippingOptionIds.includes(optId);
    const ownShippingOptionIds = has ? cur.ownShippingOptionIds.filter(o => o !== optId) : [...cur.ownShippingOptionIds, optId];
    get().patchMenu(id, { ownShippingOptionIds });
  },
  setOwnStockQty: (id, key, qty) => {
    const cur = get()[id];
    const ownStock = { ...(cur.ownStock || {}) };
    if (qty === null || Number.isNaN(qty)) delete ownStock[key];
    else ownStock[key] = Math.max(0, Math.floor(qty));
    get().patchMenu(id, { ownStock });
  },
  reload: () => {
    const fresh = load();
    set({ ...fresh, _rev: get()._rev + 1 });
  },
}));

/* ───────────── Functional helpers (read-only) ───────────── */

export function getModernMenu(id: ModernMenuId): ModernMenuConfig {
  return useModernMenusStore.getState()[id];
}

export function getMenuCategories(id: ModernMenuId): string[] {
  return useModernMenusStore.getState()[id].assignedCategories;
}

/** Categories that should appear under Home (i.e. NOT assigned to either tab). */
export function getHomeOnlyCategories(allCategories: string[]): string[] {
  const s = useModernMenusStore.getState();
  const taken = new Set([...s.available.assignedCategories, ...s.offers.assignedCategories]);
  return allCategories.filter(c => !taken.has(c));
}

export function isPriceKeyHidden(id: ModernMenuId, key: string): boolean {
  return useModernMenusStore.getState()[id].hiddenPriceKeys.includes(key);
}

/**
 * Build an "effective adminSettings" view for a tab that swaps in own pricing
 * overrides while preserving the shape so devicePriceUtil works unchanged.
 *
 * - When useOwnPricing is true and an ownPriceOverride exists for a key, it
 *   replaces the global override/base price for that key.
 * - When applyMargin is false, marginPercent is forced to 0.
 * - When applyExchangeRate is false, the calling code can pass rate=1 (we
 *   surface the flag here for the caller to decide; we do NOT mutate the rate).
 *
 * Returns a shallow-merged adminSettings; original deviceMargins, deviceExtraProfits
 * and other override maps remain available as fallbacks.
 */
export function buildEffectiveAdminSettings(
  baseAdminSettings: any,
  menu: ModernMenuConfig,
): any {
  if (!menu.useOwnPricing) return baseAdminSettings;
  return {
    ...baseAdminSettings,
    marginPercent: menu.applyMargin ? baseAdminSettings.marginPercent : 0,
    priceOverrides: { ...(baseAdminSettings.priceOverrides || {}), ...menu.ownPriceOverrides },
  };
}
